import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { TicketHallLogo } from "@/components/TicketHallLogo";
import { BecomeProducerModal } from "@/components/BecomeProducerModal";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { useTranslation } from "react-i18next";

export function Footer({ compact = false }: { compact?: boolean }) {
  const { t } = useTranslation("common");
  const { role } = useAuth();
  const navigate = useNavigate();
  const [producerModalOpen, setProducerModalOpen] = useState(false);

  const handleCreateEvent = () => {
    if (role === "producer") {
      navigate("/produtor/eventos/novo");
    } else {
      setProducerModalOpen(true);
    }
  };

  return (
    <footer className={`${compact ? "mt-2 lg:mt-4" : "mt-16 lg:mt-24"} border-t border-border bg-surface`} style={{ contentVisibility: 'auto', containIntrinsicSize: 'auto 500px' }}>
      <div className="container py-12">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Brand + CTA */}
          <div className="space-y-4">
            <TicketHallLogo size="md" />
            <p className="text-sm text-muted-foreground">
              {t("footer.tagline")}
            </p>
            <Button variant="outline" size="sm" onClick={handleCreateEvent} className="gap-1.5">
              <Plus className="h-3.5 w-3.5" />
              {t("footer.cta.createEvent")}
            </Button>
          </div>

          {/* Platform links */}
          <div>
            <h2 className="font-display font-semibold text-sm mb-4">{t("footer.sections.platform.title")}</h2>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><Link to="/eventos" className="hover:text-foreground transition-colors">{t("footer.sections.platform.events")}</Link></li>
              <li><Link to="/produtores" className="hover:text-foreground transition-colors">{t("footer.sections.platform.producers")}</Link></li>
              <li><Link to="/blog" className="hover:text-foreground transition-colors">{t("footer.sections.platform.blog")}</Link></li>
              <li><Link to="/changelog" className="hover:text-foreground transition-colors">{t("footer.sections.platform.changelog")}</Link></li>
            </ul>
          </div>

          {/* Legal + Support */}
          <div>
            <h2 className="font-display font-semibold text-sm mb-4">{t("footer.sections.legal.title")}</h2>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><Link to="/termos-de-uso/cliente" className="hover:text-foreground transition-colors">{t("footer.sections.legal.customerTerms")}</Link></li>
              <li><Link to="/termos-de-uso/produtor" className="hover:text-foreground transition-colors">{t("footer.sections.legal.producerTerms")}</Link></li>
              <li><Link to="/politica-de-privacidade" className="hover:text-foreground transition-colors">{t("footer.sections.legal.privacyPolicy")}</Link></li>
              <li><Link to="/faq" className="hover:text-foreground transition-colors">{t("footer.sections.legal.faq")}</Link></li>
            </ul>
          </div>

          {/* Contact + Social */}
          <div>
            <h2 className="font-display font-semibold text-sm mb-4">{t("footer.sections.contact.title")}</h2>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><a href="mailto:suporte@iomob.com" className="hover:text-foreground transition-colors">{t("footer.sections.contact.email")}</a></li>
            </ul>
            <div className="flex items-center gap-3 mt-4">
              <a href="https://instagram.com/tickethallbrasil" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground transition-colors" aria-label={t("footer.social.instagramLabel")}>
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
              </a>
              <a href="https://linkedin.com/company/tickethall" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground transition-colors" aria-label={t("footer.social.linkedinLabel")}>
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
              </a>
            </div>
          </div>
        </div>
        <div className="border-t border-border mt-8 pt-8 text-center text-sm text-muted-foreground">
          {t("footer.copyright")}
        </div>
      </div>

      <BecomeProducerModal open={producerModalOpen} onOpenChange={setProducerModalOpen} />
    </footer>
  );
}
